import { useState } from 'react';
import { CalendarHeart, Baby, ArrowLeft, Info } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { cn } from '@/lib/utils';

interface DueDateStepProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  onNext: () => void;
  onBack: () => void;
  babyAlreadyBorn?: boolean;
  onBabyAlreadyBornChange?: (born: boolean) => void;
  birthDate?: Date | null;
  onBirthDateChange?: (date: Date | null) => void;
}

export function DueDateStep({
  value,
  onChange,
  onNext,
  onBack,
  babyAlreadyBorn = false,
  onBabyAlreadyBornChange,
  birthDate,
  onBirthDateChange,
}: DueDateStepProps) {
  const { t } = useTranslation('onboarding');
  const [dueDateString, setDueDateString] = useState(
    value ? value.toISOString().split('T')[0] : ''
  );
  const [birthDateString, setBirthDateString] = useState(
    birthDate ? birthDate.toISOString().split('T')[0] : ''
  );
  const [error, setError] = useState<string | null>(null);

  // Calculate min/max dates for due date (roughly 1-40 weeks from now)
  const today = new Date();
  const minDueDate = new Date(today);
  minDueDate.setDate(today.getDate() + 7);
  const maxDueDate = new Date(today);
  maxDueDate.setDate(today.getDate() + 280);

  // Birth date constraints: max 2 years ago, not in the future
  const minBirthDate = new Date(today);
  minBirthDate.setFullYear(today.getFullYear() - 2);
  const maxBirthDate = today;

  const handleModeSwitch = (born: boolean) => {
    if (onBabyAlreadyBornChange) {
      onBabyAlreadyBornChange(born);
    }
    setError(null);
    // Clear the other mode's date when switching
    if (born) {
      setDueDateString('');
      onChange(null);
    } else {
      setBirthDateString('');
      onBirthDateChange?.(null);
    }
  };

  const handleDueDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDateString = e.target.value;
    setDueDateString(newDateString);
    setError(null);

    if (newDateString) {
      const date = new Date(newDateString);
      if (date < minDueDate) {
        setError(t('dueDate.errorPast'));
        onChange(null);
      } else if (date > maxDueDate) {
        setError(t('dueDate.errorFuture'));
        onChange(null);
      } else {
        onChange(date);
      }
    } else {
      onChange(null);
    }
  };

  const handleBirthDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDateString = e.target.value;
    setBirthDateString(newDateString);
    setError(null);

    if (newDateString) {
      const date = new Date(newDateString);
      if (date > maxBirthDate) {
        setError(t('dueDate.errorBornFuture'));
        onBirthDateChange?.(null);
      } else if (date < minBirthDate) {
        setError(t('dueDate.errorBornTooOld'));
        onBirthDateChange?.(null);
      } else {
        onBirthDateChange?.(date);
      }
    } else {
      onBirthDateChange?.(null);
    }
  };

  const handleNext = () => {
    if (babyAlreadyBorn) {
      if (!birthDate) {
        setError(t('dueDate.errorRequiredBorn'));
        return;
      }
    } else {
      if (!value) {
        setError(t('dueDate.errorRequired'));
        return;
      }
    }
    onNext();
  };

  // Calculate current SSW if due date is set
  const calculateSSW = () => {
    if (!value || babyAlreadyBorn) return null;
    const now = new Date();
    const diffTime = value.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const ssw = 40 - Math.ceil(diffDays / 7);
    return Math.max(1, Math.min(40, ssw));
  };

  // Calculate baby age in days
  const calculateBabyAgeDays = () => {
    if (!birthDate || !babyAlreadyBorn) return null;
    const now = new Date();
    const diffTime = now.getTime() - birthDate.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  const currentSSW = calculateSSW();
  const babyAgeDays = calculateBabyAgeDays();
  const isNextEnabled = babyAlreadyBorn ? !!birthDate : !!value;

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-white/60 hover:text-white mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('common.back')}
      </button>

      {/* Icon */}
      <div className="w-16 h-16 mx-auto mb-6 bg-pp-orange/20 rounded-full flex items-center justify-center">
        {babyAlreadyBorn ? (
          <Baby className="w-8 h-8 text-pp-orange" />
        ) : (
          <CalendarHeart className="w-8 h-8 text-pp-orange" />
        )}
      </div>

      {/* Title */}
      <h1 className="text-2xl font-bold text-white mb-2 text-center">
        {babyAlreadyBorn ? t('dueDate.titleBorn') : t('dueDate.title')}
      </h1>

      {/* Description */}
      <p className="text-white/70 mb-6 text-center">
        {babyAlreadyBorn ? t('dueDate.descriptionBorn') : t('dueDate.description')}
      </p>

      {/* Toggle: Still pregnant / Baby already born */}
      {onBabyAlreadyBornChange && (
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => handleModeSwitch(false)}
            className={cn(
              'flex-1 py-2.5 px-3 rounded-[10px] text-sm font-medium transition-all',
              !babyAlreadyBorn
                ? 'bg-pp-orange text-white'
                : 'bg-white/10 text-white/60 hover:bg-white/15'
            )}
          >
            {t('dueDate.notYetBorn')}
          </button>
          <button
            onClick={() => handleModeSwitch(true)}
            className={cn(
              'flex-1 py-2.5 px-3 rounded-[10px] text-sm font-medium transition-all',
              babyAlreadyBorn
                ? 'bg-pp-orange text-white'
                : 'bg-white/10 text-white/60 hover:bg-white/15'
            )}
          >
            {t('dueDate.alreadyBorn')}
          </button>
        </div>
      )}

      {/* Date Input */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-white/80 mb-2">
          {babyAlreadyBorn ? t('dueDate.labelBorn') : t('dueDate.label')}
        </label>
        {babyAlreadyBorn ? (
          <input
            type="date"
            value={birthDateString}
            onChange={handleBirthDateChange}
            min={minBirthDate.toISOString().split('T')[0]}
            max={maxBirthDate.toISOString().split('T')[0]}
            className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20
                       text-white caret-white
                       focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                       outline-none transition-all"
          />
        ) : (
          <input
            type="date"
            value={dueDateString}
            onChange={handleDueDateChange}
            min={minDueDate.toISOString().split('T')[0]}
            max={maxDueDate.toISOString().split('T')[0]}
            className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20
                       text-white caret-white
                       focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                       outline-none transition-all"
          />
        )}
        {error && (
          <p className="mt-2 text-sm text-red-400">{error}</p>
        )}
      </div>

      {/* SSW Preview (pregnancy mode) */}
      {!babyAlreadyBorn && currentSSW && (
        <div className="bg-pp-orange/20 rounded-[10px] p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-pp-orange mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-white">
                {t('dueDate.currentSsw', { ssw: currentSSW })}
              </p>
              <p className="text-sm text-white/70 mt-1">
                {t('dueDate.weeksRemaining', { weeks: 40 - currentSSW })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Baby Age Preview (born mode) */}
      {babyAlreadyBorn && babyAgeDays !== null && (
        <div className="bg-pp-orange/20 rounded-[10px] p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-pp-orange mt-0.5 flex-shrink-0" />
            <p className="font-medium text-white">
              {t('dueDate.babyAge', { days: babyAgeDays })}
            </p>
          </div>
        </div>
      )}

      {/* CTA */}
      <button
        onClick={handleNext}
        disabled={!isNextEnabled}
        className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold hover:bg-pp-orange/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {t('common.next')}
      </button>
    </div>
  );
}
